import { ApplicationStatus, EmploymentType, InterviewStatus } from '../../types';
import { formatDate } from '../../utils/formatDate';

export interface ColumnMeta {
  label: string;
  accent: string;
  badge: string;
  border: string;
  iconBg: string;
  text: string;
}

export const COLUMN_META: Record<ApplicationStatus, ColumnMeta> = {
  APPLIED: {
    label: 'Applied',
    accent: 'bg-zinc-400',
    badge: 'bg-zinc-100 text-zinc-600',
    border: 'border-t-zinc-400',
    iconBg: 'bg-zinc-50 text-zinc-500',
    text: 'text-zinc-600',
  },
  SCREENING: {
    label: 'Screening',
    accent: 'bg-sky-400',
    badge: 'bg-sky-50 text-sky-700',
    border: 'border-t-sky-400',
    iconBg: 'bg-sky-50 text-sky-600',
    text: 'text-sky-700',
  },
  TECHNICAL: {
    label: 'Technical',
    accent: 'bg-violet-400',
    badge: 'bg-violet-50 text-violet-700',
    border: 'border-t-violet-400',
    iconBg: 'bg-violet-50 text-violet-600',
    text: 'text-violet-700',
  },
  FINAL: {
    label: 'Final',
    accent: 'bg-indigo-400',
    badge: 'bg-indigo-50 text-indigo-700',
    border: 'border-t-indigo-400',
    iconBg: 'bg-indigo-50 text-indigo-600',
    text: 'text-indigo-700',
  },
  OFFER: {
    label: 'Offer',
    accent: 'bg-emerald-500',
    badge: 'bg-emerald-50 text-emerald-700',
    border: 'border-t-emerald-500',
    iconBg: 'bg-emerald-50 text-emerald-600',
    text: 'text-emerald-700',
  },
  HIRED: {
    label: 'Hired',
    accent: 'bg-emerald-600',
    badge: 'bg-emerald-100 text-emerald-800',
    border: 'border-t-emerald-600',
    iconBg: 'bg-emerald-100 text-emerald-700',
    text: 'text-emerald-800',
  },
  REJECTED: {
    label: 'Rejected',
    accent: 'bg-red-400',
    badge: 'bg-red-50 text-red-600',
    border: 'border-t-red-400',
    iconBg: 'bg-red-50 text-red-500',
    text: 'text-red-600',
  },
  WITHDRAWN: {
    label: 'Withdrawn',
    accent: 'bg-zinc-300',
    badge: 'bg-zinc-100 text-zinc-500',
    border: 'border-t-zinc-300',
    iconBg: 'bg-zinc-50 text-zinc-400',
    text: 'text-zinc-500',
  },
};

export const COLUMN_STATI = Object.keys(COLUMN_META) as ApplicationStatus[];

export const EMPLOYMENT_LABELS: Record<EmploymentType, string> = {
  FULL_TIME: 'Full-time',
  PART_TIME: 'Part-time',
  CONTRACT: 'Contract',
  INTERNSHIP: 'Internship',
  FREELANCE: 'Freelance',
  OTHER: 'Other',
};

export const INTERVIEW_TYPE_LABELS: Record<string, string> = {
  PHONE: 'Phone',
  VIDEO: 'Video',
  ONSITE: 'On-site',
  CODING: 'Coding',
  PANEL: 'Panel',
  TEXT: 'Text',
};

export const INTERVIEW_STATUS_META: Record<InterviewStatus, { label: string; badge: string }> = {
  SCHEDULED: { label: 'Scheduled', badge: 'bg-indigo-50 text-indigo-700' },
  COMPLETED: { label: 'Completed', badge: 'bg-emerald-50 text-emerald-700' },
  CANCELLED: { label: 'Cancelled', badge: 'bg-zinc-100 text-zinc-500' },
};

export const INTERVIEW_ROUNDS = [
  'Phone screen',
  'Recruiter call',
  'Technical screen',
  'Technical interview',
  'On-site',
  'Pair programming',
  'System design',
  'Behavioral',
  'Team fit',
  'Panel',
  'Final round',
];

export interface ResumeOption {
  id: number;
  label: string;
}

export const resumeLabel = (resume: {
  id: number;
  personalInfo?: { fullName?: string };
  templateId?: string;
  createdAt?: string;
}): string => {
  const name = resume.personalInfo?.fullName?.trim() || `${resume.templateId ?? 'resume'} #${resume.id}`;
  const date = resume.createdAt ? formatDate(resume.createdAt) : '';
  if (resume.personalInfo?.fullName?.trim()) {
    return date ? `${name} — ${resume.templateId ?? 'resume'} · ${date}` : `${name} — ${resume.templateId ?? 'resume'}`;
  }
  return date ? `${name} · ${date}` : name;
};